import { Head, Link, router } from '@inertiajs/react'
import { Plus, Edit, Trash2, Command, ArrowLeft, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import { stripHtml } from '@/lib/sanitizeHtml'

interface Macro {
  id: number
  name: string
  content: string
  usage_count: number
  created_at: string
  updated_at: string
}

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface MacrosIndexProps {
  macros: Macro[]
  currentAgent: Agent
}

export default function MacrosIndex({ macros, currentAgent }: MacrosIndexProps) {
  const [macroQuery, setMacroQuery] = useState('')

  const filteredMacros = useMemo(() => {
    const normalizedQuery = macroQuery.trim().toLowerCase()
    if (!normalizedQuery) return macros

    return macros.filter((macro) => {
      const nameMatch = macro.name.toLowerCase().includes(normalizedQuery)
      const contentMatch = stripHtml(macro.content).toLowerCase().includes(normalizedQuery)
      return nameMatch || contentMatch
    })
  }, [macroQuery, macros])

  const handleDelete = (id: number, name: string) => {
    if (confirm(`Tem certeza que deseja excluir "${name}"?`)) {
      router.delete(`/macros/${id}`)
    }
  }

  return (
    <>
      <Head title="Macros" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6">
            <div className="flex items-center">
              <Link
                href="/inbox"
                className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
            </div>
            <Link
              href="/macros/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Novo Macro
            </Link>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="macros" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-3xl">
                {macros.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Command className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum macro ainda</h3>
                    <p className="text-gray-500 mb-4">
                      Crie macros para inserir respostas comuns rapidamente com /nome
                    </p>
                    <Link
                      href="/macros/new"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Criar seu primeiro macro
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={macroQuery}
                          onChange={(e) => setMacroQuery(e.target.value)}
                          placeholder="Filtrar por nome ou conteudo..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {filteredMacros.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                        <Command className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                        <h3 className="text-base font-medium text-gray-900 mb-1">Nenhum macro encontrado</h3>
                        <p className="text-gray-500 text-sm">
                          Tente ajustar o filtro para buscar no nome ou no conteudo.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
                        {filteredMacros.map((macro) => (
                          <div key={macro.id} className="p-4 flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">{macro.name}</span>
                                {macro.usage_count > 0 && (
                                  <span className="text-xs text-gray-400">
                                    usado {macro.usage_count} vezes
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 line-clamp-2">
                                {stripHtml(macro.content)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Link
                                href={`/macros/${macro.id}/edit`}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                              </Link>
                              <button
                                onClick={() => handleDelete(macro.id, macro.name)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
